/**
 * Bold - "Select size" gating.
 *
 * The PDP renders with the size run empty, so until a size is picked there is no
 * complete variant and Add to cart must not be usable. The theme's own variant JS
 * does disable the button in that state, but labels it with
 * variantStrings.unavailable ("Unavailable"), which reads as "we don't sell this"
 * rather than "you haven't chosen yet". This relabels only that case.
 *
 * It never enables a button the theme disabled for a real reason (sold out, no
 * such combination) - it only overrides the TEXT while options are incomplete,
 * and otherwise stays out of the way.
 */
(() => {
  const LABEL = () => window.variantStrings?.selectSize || 'Select size';

  const pickers = () => document.querySelectorAll('product-variant-selects, .product__variant-picker');

  // Every radio group present, and whether each has something checked.
  const missingSelection = () => {
    const root = document.querySelector('.product__variant-picker');
    if (!root) return false;
    const groups = new Set();
    root.querySelectorAll('.product-option__input').forEach(i => groups.add(i.name));
    if (!groups.size) return false;
    return [...groups].some(name => !root.querySelector(`.product-option__input[name="${CSS.escape(name)}"]:checked`));
  };

  // Every write here is conditional. The MutationObserver below watches the sticky
  // bar with subtree:true and the submit button sits inside it, so an
  // unconditional `span.textContent = ...` replaces the text node, fires the
  // observer, and re-enters this function forever. Observer callbacks are
  // microtasks, so that starves the queue and freezes the page rather than
  // erroring. Writing only on an actual change means no mutation is emitted at all.
  const setText = (el, text) => { if (el.textContent !== text) el.textContent = text; };

  const apply = () => {
    const incomplete = missingSelection();
    document.querySelectorAll('.product-form__submit, .product-bar__submit').forEach(btn => {
      const span = btn.querySelector('span') || btn;
      if (incomplete) {
        if (!btn.hasAttribute('disabled')) btn.setAttribute('disabled', 'disabled');
        setText(span, LABEL());
        if (btn.dataset.boldSelectSize !== 'true') btn.dataset.boldSelectSize = 'true';
      } else if (btn.dataset.boldSelectSize) {
        // Hand control back to the theme: it owns the label from here.
        delete btn.dataset.boldSelectSize;
        btn.removeAttribute('disabled');
        if (span.textContent.trim() === LABEL()) {
          setText(span, window.variantStrings?.addToCart || 'Add to cart');
        }
      }
    });
  };

  // Re-assert after the theme's own variant handler, which runs on an async fetch.
  // The theme replaces #ProductStickyCart wholesale on every variant change, so
  // the proxies are re-rendered and lose their pressed state - resync with each pass.
  const pass = () => { apply(); syncProxies(); };
  const settle = () => { pass(); requestAnimationFrame(pass); [60, 200, 450].forEach(ms => setTimeout(pass, ms)); };


  // ---------------------------------------------------------------------------
  // Sticky-bar option proxies. They are buttons, not radios, so clicking one has
  // to drive the real input in the main picker; the theme's variant JS then runs
  // exactly as it would have. Matching is by option name prefix plus value,
  // because the rendered input names carry a position suffix (and a newline).
  // ---------------------------------------------------------------------------
  const realInput = (opt, val) => {
    const root = document.querySelector('.product__variant-picker');
    if (!root) return null;
    return [...root.querySelectorAll('.product-option__input')].find(
      i => i.name.trim().toLowerCase().startsWith(opt.toLowerCase()) && i.value.trim() === val
    );
  };

  const syncProxies = () => {
    // Bail rather than guess: mid-swap the picker can be momentarily absent, and
    // writing '' then would reset every dropdown to its placeholder.
    if (!document.querySelector('.product__variant-picker .product-option__input')) return;
    document.querySelectorAll('.bold-stickybar__select').forEach(sel => {
      const checked = [...document.querySelectorAll('.product__variant-picker .product-option__input:checked')]
        .find(i => i.name.trim().toLowerCase().startsWith(sel.dataset.boldOpt.toLowerCase()));
      sel.value = checked ? checked.value.trim() : '';
      // Mark sold-out combinations in the list rather than hiding them.
      [...sel.options].forEach(o => {
        if (!o.value) return;
        const input = realInput(sel.dataset.boldOpt, o.value);
        o.disabled = !!input?.classList.contains('disabled');
      });
    });
  };

  document.addEventListener('change', e => {
    const sel = e.target.closest?.('.bold-stickybar__select');
    if (!sel || !sel.value) return;
    const input = realInput(sel.dataset.boldOpt, sel.value);
    if (!input || input.checked) return;
    // The theme replaces #ProductStickyCart wholesale on every variant change, so
    // this very <select> is destroyed and rebuilt. Focus is returned to its
    // replacement, or it would land on <body> and strand a keyboard user - the
    // theme's own focus call now runs with preventScroll, so it no longer pulls
    // the viewport up to the main picker.
    const opt = sel.dataset.boldOpt;
    const refocus = () => {
      const fresh = document.querySelector(`.bold-stickybar__select[data-bold-opt="${CSS.escape(opt)}"]`);
      if (fresh && document.activeElement !== fresh && document.activeElement === document.body) fresh.focus({ preventScroll: true });
    };

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));

    [60, 200, 450].forEach(ms => setTimeout(refocus, ms));
    settle();
  });

  document.addEventListener('change', e => {
    if (e.target.closest?.('.product-option__input')) requestAnimationFrame(syncProxies);
  });

  document.addEventListener('change', e => {
    if (e.target.closest?.('.product-option__input')) settle();
  });
  document.addEventListener('product-info:loaded', settle);

  // The theme replaces #ProductStickyCart's innerHTML after its variant fetch
  // resolves, which restores the server-rendered markup - placeholder selected.
  // That lands whenever the network says so, routinely later than the 450ms
  // re-assert, which is why the dropdowns reset "randomly". Watching the subtree
  // removes the guesswork. Setting .value does not mutate childList or
  // attributes, so this cannot feed itself.
  // Publish the bar's real height so the back-to-top button can clear it. Measured
  // rather than hardcoded: the bar's height follows its own padding and the button's
  // min-height, both of which differ by breakpoint. Written only on change - this
  // sets an attribute on <html>, and an unconditional write inside an observer
  // callback is how the last freeze happened.
  const publishHeight = bar => {
    const h = Math.round(bar.getBoundingClientRect().height) + 'px';
    const root = document.documentElement;
    if (root.style.getPropertyValue('--bold-stickybar-h') !== h) {
      root.style.setProperty('--bold-stickybar-h', h);
    }
  };

  const watchBar = () => {
    const bar = document.querySelector('.product__sticky-cart');
    if (!bar || bar.dataset.boldWatched) return;
    bar.dataset.boldWatched = 'true';

    publishHeight(bar);
    if (window.ResizeObserver) new ResizeObserver(() => publishHeight(bar)).observe(bar);
    let inPass = false;
    new MutationObserver(() => {
      if (inPass) return;
      inPass = true;
      try { apply(); syncProxies(); publishHeight(bar); } finally { inPass = false; }
    }).observe(bar, { childList: true, subtree: true });
  };

  const start = () => { settle(); syncProxies(); watchBar(); };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
